import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { apiRequest } from '../api/client'
import { useAuth } from '../auth/AuthContext'

const STATUS_STYLES = {
  submitted:                'bg-slate-50 text-slate-700 border-slate-200',
  under_review:             'bg-sky-50 text-sky-800 border-sky-200',
  pending_company_match:    'bg-indigo-50 text-indigo-800 border-indigo-200',
  ai_reviewed:              'bg-violet-50 text-violet-800 border-violet-200',
  approved:                 'bg-emerald-50 text-emerald-800 border-emerald-200',
  rejected:                 'bg-rose-50 text-rose-800 border-rose-200',
  hq_review:                'bg-amber-50 text-amber-800 border-amber-200',
  fraud_review:             'bg-red-100 text-red-900 border-red-300',
  shopify_customer_deleted: 'bg-orange-50 text-orange-800 border-orange-200',
  revoked:                  'bg-stone-100 text-stone-800 border-stone-300',
  re_verification_requested:'bg-cyan-50 text-cyan-800 border-cyan-200',
  request_to_delete:        'bg-red-50 text-red-800 border-red-200'
}

const TYPE_STYLES = {
  trade:       'bg-brand-50 text-brand-700 border-brand-200',
  hospitality: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200'
}

// BBE-234: Shopify sync outcome, separate from the approve/revoke status.
const SYNC_STYLES = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  synced:  'bg-emerald-50 text-emerald-800 border-emerald-200',
  failed:  'bg-red-100 text-red-900 border-red-300'
}

const SYNC_LABELS = {
  pending: 'sync pending',
  synced:  'synced',
  failed:  'sync failed'
}

const REVIEWER_ROLES = ['reviewer', 'hq_reviewer', 'admin']
const HQ_ROLES       = ['hq_reviewer', 'admin']
const ADMIN_ROLES    = ['admin']

const APPROVE_FROM = ['submitted', 'under_review', 'pending_company_match', 'ai_reviewed', 're_verification_requested']
const REJECT_FROM  = ['submitted', 'under_review', 'pending_company_match', 'ai_reviewed', 'hq_review', 'fraud_review', 're_verification_requested']
const FLAG_HQ_FROM    = ['submitted', 'under_review', 'pending_company_match', 'ai_reviewed', 're_verification_requested']
const FLAG_FRAUD_FROM = ['submitted', 'under_review', 'pending_company_match', 'ai_reviewed', 'hq_review', 're_verification_requested']
const UNFLAG_FROM = ['hq_review', 'fraud_review']
// Destination (Trade/DTC vs Hospitality/B4B) can be switched only before the
// application has synced to Shopify — mirrors TradeApplication::TYPE_SWITCHABLE_STATES.
const SWITCH_DEST_FROM = [
  'submitted', 'under_review', 'pending_company_match', 'ai_reviewed',
  'hq_review', 'fraud_review', 're_verification_requested'
]
const destLabel = (t) => (t === 'hospitality' ? 'Hospitality (B4B)' : 'Trade (DTC)')
// Any state EXCEPT request_to_delete itself can be flagged for deletion.
const REQUEST_DELETION_FROM = [
  'submitted', 'under_review', 'pending_company_match', 'ai_reviewed',
  'approved', 'rejected', 'hq_review', 'fraud_review',
  'shopify_customer_deleted', 'revoked', 're_verification_requested'
]

function Badge({ value, styles }) {
  if (!value) return null
  const cls = styles[value] || 'bg-slate-50 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${cls}`}>
      {value.replace(/_/g, ' ')}
    </span>
  )
}

function Section({ title, children }) {
  return (
    <div className="card p-5 sm:p-6">
      <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">{title}</h3>
      <dl className="divide-y divide-slate-100">{children}</dl>
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 py-2.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-900 sm:col-span-2 break-words">{children ?? '—'}</dd>
    </div>
  )
}

// BBE-234: badge for the Shopify sync outcome. Renders nothing when a sync was
// never attempted (sync_state null), so it only shows for approved apps.
function SyncBadge({ state }) {
  if (!state) return null
  const cls = SYNC_STYLES[state] || 'bg-slate-50 text-slate-700 border-slate-200'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${cls}`}>
      {SYNC_LABELS[state] || state}
    </span>
  )
}

// BBE-234: inline warning shown on the specific contact field Shopify rejected
// (e.g. email/phone), so the operator knows exactly what to fix / who to
// contact. Read-only — no retry.
function SyncFieldWarning({ application, field }) {
  if (application.sync_state !== 'failed' || application.sync_error_field !== field) return null
  return (
    <p className="mt-1 text-xs text-red-700">
      ⚠ Shopify rejected this field{application.sync_error_message ? `: ${application.sync_error_message}` : ''}
    </p>
  )
}

function classifyDocument(d) {
  const ct = (d.content_type || '').toLowerCase()
  const ext = (d.filename || '').split('.').pop().toLowerCase()
  const isImage = ct.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(ext)
  const isPdf   = ct === 'application/pdf' || ext === 'pdf'
  return { isImage, isPdf, ext }
}

function DocumentPreview({ document: d, onOpen }) {
  const { isImage, isPdf, ext } = classifyDocument(d)
  const previewable = isImage || isPdf

  return (
    <li className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => previewable && onOpen(d)}
        disabled={!previewable}
        className={`block w-full text-left group ${previewable ? 'cursor-zoom-in' : 'cursor-default'}`}
        aria-label={previewable ? `Open ${d.filename}` : d.filename}
      >
        {isImage ? (
          <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
            <img
              src={d.signed_url}
              alt={d.filename}
              loading="lazy"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              className="max-h-full max-w-full object-contain group-hover:scale-[1.02] transition select-none"
            />
          </div>
        ) : isPdf ? (
          <div className="aspect-video bg-slate-100 relative">
            <object
              data={`${d.signed_url}#toolbar=0&navpanes=0&view=FitH`}
              type="application/pdf"
              className="w-full h-full pointer-events-none"
              aria-label={d.filename}
            >
              <div className="w-full h-full flex items-center justify-center text-slate-400 text-xs">PDF preview unavailable</div>
            </object>
            <div className="absolute inset-0 bg-transparent group-hover:bg-slate-900/5 transition" />
            <span className="absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] font-semibold">PDF</span>
          </div>
        ) : (
          <div className="aspect-video bg-slate-100 flex flex-col items-center justify-center text-slate-500">
            <svg className="w-10 h-10 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs uppercase tracking-wider">{ext || 'file'}</span>
          </div>
        )}
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 text-xs">
          <div className="font-medium text-slate-700 truncate" title={d.filename}>{d.filename}</div>
          <div className="text-slate-500 mt-0.5">
            {d.content_type} · {(d.byte_size / 1024).toFixed(0)} KB
            {d.document_type && ` · ${d.document_type}`}
          </div>
        </div>
      </button>
    </li>
  )
}

function DocumentViewer({ document: d, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    const prevOverflow = window.document.body.style.overflow
    window.document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handler)
      window.document.body.style.overflow = prevOverflow
    }
  }, [onClose])

  if (!d) return null
  const { isImage, isPdf } = classifyDocument(d)

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm flex flex-col"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={d.filename}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3 text-white">
        <div className="min-w-0">
          <div className="font-medium truncate" title={d.filename}>{d.filename}</div>
          <div className="text-xs text-slate-300 truncate">
            {d.content_type} · {(d.byte_size / 1024).toFixed(0)} KB
            {d.document_type && ` · ${d.document_type}`}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md bg-white/10 hover:bg-white/20 transition"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div
        className="flex-1 flex items-center justify-center p-4 sm:p-8 min-h-0"
        onClick={(e) => e.stopPropagation()}
      >
        {isImage ? (
          <img
            src={d.signed_url}
            alt={d.filename}
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className="max-h-full max-w-full object-contain rounded shadow-2xl select-none"
          />
        ) : isPdf ? (
          <object
            data={`${d.signed_url}#toolbar=0&navpanes=0&view=FitH`}
            type="application/pdf"
            className="w-full h-full max-w-5xl bg-white rounded shadow-2xl"
            aria-label={d.filename}
          >
            <div className="w-full h-full flex items-center justify-center text-slate-400 bg-white text-sm">PDF preview unavailable in this browser.</div>
          </object>
        ) : (
          <div className="text-slate-300">No inline preview for this file type.</div>
        )}
      </div>
    </div>
  )
}

function ReviewActions({ application, onChange }) {
  const { user } = useAuth()
  const [action, setAction]   = useState(null)
  const [notes,  setNotes]    = useState('')
  const [notify, setNotify]   = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const isReviewer = user && REVIEWER_ROLES.includes(user.role)
  const isHq       = user && HQ_ROLES.includes(user.role)
  const status = application.status
  const otherType = application.application_type === 'trade' ? 'hospitality' : 'trade'

  const available = {
    approve:      isReviewer && APPROVE_FROM.includes(status),
    reject:       isReviewer && REJECT_FROM.includes(status),
    switch_dest:  isReviewer && SWITCH_DEST_FROM.includes(status),
    flag_hq:      isReviewer && FLAG_HQ_FROM.includes(status),
    flag_fraud:   isReviewer && FLAG_FRAUD_FROM.includes(status),
    unflag:       isHq && UNFLAG_FROM.includes(status),
    revoke:       isHq && status === 'approved',
    re_verify:    isHq && status === 'approved'
  }

  const anyAvailable = Object.values(available).some(Boolean)
  if (!anyAvailable) return null

  const reset = () => {
    setAction(null)
    setNotes('')
    setNotify(false)
    setError('')
  }

  const run = async () => {
    setSubmitting(true)
    setError('')
    try {
      const id = application.id
      let res
      if (action === 'approve')      res = await apiRequest(`/api/v1/trade_applications/${id}/approve`,  { method: 'PATCH', body: { notes } })
      else if (action === 'reject') res = await apiRequest(`/api/v1/trade_applications/${id}/reject`,   { method: 'PATCH', body: { notes } })
      else if (action === 'switch_dest') res = await apiRequest(`/api/v1/trade_applications/${id}/switch_destination`, { method: 'PATCH', body: { application_type: otherType } })
      else if (action === 'flag_hq')    res = await apiRequest(`/api/v1/trade_applications/${id}/flag`, { method: 'PATCH', body: { kind: 'hq_review',    reason: notes } })
      else if (action === 'flag_fraud') res = await apiRequest(`/api/v1/trade_applications/${id}/flag`, { method: 'PATCH', body: { kind: 'fraud_review', reason: notes } })
      else if (action === 'unflag') res = await apiRequest(`/api/v1/trade_applications/${id}/unflag`,   { method: 'PATCH', body: { notes } })
      else if (action === 'revoke') res = await apiRequest(`/api/v1/trade_applications/${id}/revoke`,   { method: 'PATCH', body: { reason: notes, notify_customer: notify } })
      else if (action === 're_verify') res = await apiRequest(`/api/v1/trade_applications/${id}/re_verify`, { method: 'POST',  body: { reason: notes } })
      onChange(res.trade_application)
      reset()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const placeholder = {
    approve:    'Optional notes',
    reject:     'Reason for rejection',
    flag_hq:    'Why this needs HQ review',
    flag_fraud: 'Suspected fraud — details',
    unflag:     'Optional notes',
    revoke:     'Reason for revocation',
    re_verify:  'Reason for re-verification'
  }

  return (
    <div className="card p-5 sm:p-6">
      <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">Review actions</h3>

      {error && <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>}

      {!action ? (
        <div className="flex flex-wrap gap-2">
          {available.approve    && <button onClick={() => setAction('approve')}     className="btn-primary">Approve</button>}
          {available.reject     && <button onClick={() => setAction('reject')}      className="btn-secondary">Reject</button>}
          {available.switch_dest && <button onClick={() => setAction('switch_dest')} className="btn-secondary">Switch to {destLabel(otherType)}</button>}
          {available.flag_hq    && <button onClick={() => setAction('flag_hq')}     className="btn-secondary">Flag for HQ review</button>}
          {available.flag_fraud && <button onClick={() => setAction('flag_fraud')}  className="btn-secondary">Flag for fraud review</button>}
          {available.unflag     && <button onClick={() => setAction('unflag')}      className="btn-secondary">Clear flag</button>}
          {available.revoke    && <button onClick={() => setAction('revoke')}    className="btn-secondary">Revoke</button>}
          {available.re_verify && <button onClick={() => setAction('re_verify')} className="btn-secondary">Request re-verification</button>}
        </div>
      ) : (
        <div>
          {action === 'switch_dest' ? (
            <p className="text-sm text-slate-700 mb-3">
              Move this submission to <strong>{destLabel(otherType)}</strong>? It will sync to that store when approved. This only changes the destination — it can be switched back while the application is still pending.
            </p>
          ) : (
            <p className="text-sm text-slate-700 mb-2">
              Confirm <strong className="capitalize">{action.replace(/_/g, ' ')}</strong>
              {action === 're_verify' && ' — creates a new application linked to this one'}
            </p>
          )}
          {action !== 'switch_dest' && (
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input w-full mb-3"
              placeholder={placeholder[action]}
            />
          )}
          {action === 'revoke' && (
            <label className="flex items-center gap-2 text-sm mb-3">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
              Notify customer via email
            </label>
          )}
          <div className="flex gap-2">
            <button onClick={run} disabled={submitting} className="btn-primary">
              {submitting ? 'Working…' : 'Confirm'}
            </button>
            <button onClick={reset} disabled={submitting} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

function DangerZone({ application, onChange, onDeleted }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [action, setAction] = useState(null)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const isReviewer = user && REVIEWER_ROLES.includes(user.role)
  const isAdmin    = user && ADMIN_ROLES.includes(user.role)
  const status     = application.status

  const available = {
    request_deletion: isReviewer && REQUEST_DELETION_FROM.includes(status),
    cancel_deletion:  isAdmin && status === 'request_to_delete',
    delete:           isAdmin && status === 'request_to_delete'
  }

  if (!Object.values(available).some(Boolean)) return null

  const requestMeta = application.metadata || {}
  const isPendingDeletion = status === 'request_to_delete'

  const reset = () => {
    setAction(null)
    setReason('')
    setError('')
  }

  const submit = async () => {
    setSubmitting(true)
    setError('')
    try {
      const id = application.id
      if (action === 'delete') {
        await apiRequest(`/api/v1/trade_applications/${id}`, { method: 'DELETE', body: { reason } })
        if (onDeleted) onDeleted()
        navigate('/trade_applications')
        return
      }
      let res
      if (action === 'request_deletion') {
        res = await apiRequest(`/api/v1/trade_applications/${id}/request_deletion`,        { method: 'PATCH', body: { reason } })
      } else if (action === 'cancel_deletion') {
        res = await apiRequest(`/api/v1/trade_applications/${id}/cancel_deletion_request`, { method: 'PATCH', body: { notes: reason } })
      }
      if (res && onChange) onChange(res.trade_application)
      reset()
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const placeholder = {
    request_deletion: 'Why this should be deleted (admin will review)',
    cancel_deletion:  'Optional notes',
    delete:           'Reason for deletion (audit log)'
  }

  const confirmLabel = {
    request_deletion: 'Submit request',
    cancel_deletion:  'Confirm cancel',
    delete:           'Yes, delete permanently'
  }

  const submittingLabel = {
    request_deletion: 'Submitting…',
    cancel_deletion:  'Submitting…',
    delete:           'Deleting…'
  }

  return (
    <div className="card p-5 sm:p-6 border-red-300 bg-red-50/40">
      {isPendingDeletion && (
        <>
          <p className="text-sm text-slate-700 mb-1">
            This application has been flagged for deletion
            {requestMeta.deletion_requested_by_email && (
              <> by <strong>{requestMeta.deletion_requested_by_email}</strong></>
            )}
            {requestMeta.deletion_requested_at && (
              <> on {new Date(requestMeta.deletion_requested_at).toLocaleString()}</>
            )}.
          </p>
          {requestMeta.deletion_request_reason && (
            <p className="text-sm text-slate-600 mb-3">Reason: <em>"{requestMeta.deletion_request_reason}"</em></p>
          )}
          <p className="text-xs text-slate-600 mb-4">
            Deleting removes the application row, its uploaded documents, and the S3 files.
            <strong className="text-red-800"> The Shopify customer and company will NOT be deleted</strong> — clean those up manually if needed.
          </p>
        </>
      )}

      {error && (
        <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
      )}

      {!action ? (
        <div className="flex flex-wrap gap-2">
          {available.request_deletion && (
            <button
              onClick={() => setAction('request_deletion')}
              className="btn-secondary text-red-700 hover:bg-red-50"
            >
              Request deletion
            </button>
          )}
          {available.cancel_deletion && (
            <button onClick={() => setAction('cancel_deletion')} className="btn-secondary">
              Cancel deletion request
            </button>
          )}
          {available.delete && (
            <button
              onClick={() => setAction('delete')}
              className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition"
            >
              Delete permanently
            </button>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-slate-800 mb-2">
            {action === 'delete'
              ? <>Type a short reason to confirm. <strong>This cannot be undone.</strong></>
              : <>Confirm <strong className="capitalize">{action.replace(/_/g, ' ')}</strong></>
            }
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={action === 'delete' ? 2 : 3}
            className="input w-full mb-3"
            placeholder={placeholder[action]}
          />
          <div className="flex gap-2">
            <button
              onClick={submit}
              disabled={submitting}
              className={
                action === 'delete'
                  ? 'inline-flex items-center px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50'
                  : 'btn-primary'
              }
            >
              {submitting ? submittingLabel[action] : confirmLabel[action]}
            </button>
            <button onClick={reset} disabled={submitting} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

function CompanyMatchPanel({ application, candidates, onChange }) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  if (application.status !== 'pending_company_match') return null

  const id = application.id

  const submit = async (path, body) => {
    setSubmitting(true)
    setError('')
    try {
      const { trade_application } = await apiRequest(path, { method: 'PATCH', body })
      onChange(trade_application)
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="card p-5 sm:p-6 border-indigo-200 bg-indigo-50/30">
      <h3 className="text-sm font-semibold text-indigo-900 uppercase tracking-wider mb-3">
        Company match — review needed
      </h3>
      <p className="text-sm text-slate-700 mb-4">
        We found {candidates.length} previously-approved {candidates.length === 1 ? 'company' : 'companies'} that
        look similar. Reuse one to keep the same Shopify Company, or dismiss to create a new one.
      </p>

      {error && (
        <div className="mb-3 p-2 rounded bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
      )}

      <ul className="space-y-3 mb-4">
        {candidates.map((c) => {
          const street = [c.address_line1, c.address_line2].filter(Boolean).join(', ')
          const region = [c.city, c.state, c.postal_code].filter(Boolean).join(', ')
          const locality = [region, c.country].filter(Boolean).join(' · ')
          const websiteHref = c.company_website
            ? (/^https?:\/\//i.test(c.company_website) ? c.company_website : `https://${c.company_website}`)
            : null

          return (
            <li key={c.application_id} className="border border-slate-200 rounded-lg p-4 bg-white hover:border-indigo-300 hover:shadow-sm transition">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-slate-900 truncate">{c.company_name}</div>
                    <Badge value={c.application_type} styles={TYPE_STYLES} />
                  </div>

                  {street && (
                    <div className="text-sm text-slate-700 mt-1 truncate">{street}</div>
                  )}
                  {locality && (
                    <div className="text-sm text-slate-500 truncate">{locality}</div>
                  )}

                  {websiteHref && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-block text-xs text-brand-600 hover:text-brand-700 underline mt-1.5 truncate max-w-full"
                    >
                      {c.company_website}
                    </a>
                  )}

                  <div className="text-xs text-slate-500 mt-2 truncate">
                    {c.full_name && <>{c.full_name} </>}
                    {c.email_address && <span className="text-slate-400">&lt;{c.email_address}&gt;</span>}
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-1.5">
                    <span>app #{c.application_id}</span>
                    {c.reviewed_at && (
                      <>
                        <span>·</span>
                        <span>approved {new Date(c.reviewed_at).toLocaleDateString()}</span>
                      </>
                    )}
                    <span>·</span>
                    <span title={`name ${c.name_similarity} · addr ${c.address_similarity}`}>
                      {Math.round(c.combined_score * 100)}% match
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => submit(
                    `/api/v1/trade_applications/${id}/confirm_company_match`,
                    { matched_application_id: c.application_id }
                  )}
                  disabled={submitting}
                  className="btn-primary whitespace-nowrap shrink-0"
                >
                  Use this company
                </button>
              </div>
            </li>
          )
        })}
      </ul>

      <button
        onClick={() => submit(`/api/v1/trade_applications/${id}/dismiss_company_match`, {})}
        disabled={submitting}
        className="btn-secondary"
      >
        None of these — create a new company
      </button>
    </div>
  )
}

export default function TradeApplicationShowPage() {
  const { id } = useParams()
  const [application, setApplication] = useState(null)
  const [candidates, setCandidates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewingDocument, setViewingDocument] = useState(null)

  useEffect(() => {
    apiRequest(`/api/v1/trade_applications/${id}`)
      .then(({ trade_application, company_match_candidates }) => {
        setApplication(trade_application)
        setCandidates(company_match_candidates || [])
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [id])

  // When the panel mutates the application, refetch candidates if still in pending state.
  const handleApplicationChange = async (updated) => {
    setApplication(updated)
    if (updated.status === 'pending_company_match') {
      const { company_match_candidates } = await apiRequest(`/api/v1/trade_applications/${id}`)
      setCandidates(company_match_candidates || [])
    } else {
      setCandidates([])
    }
  }

  if (loading) {
    return <p className="max-w-3xl mx-auto px-4 sm:px-6 py-10 text-slate-600">Loading…</p>
  }
  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-800">{error}</div>
      </div>
    )
  }
  if (!application) return null

  const addr = application.company_address || {}
  const history = application.status_history || []

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <Link
        to="/trade_applications"
        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 mb-4"
      >
        ← Back to applications
      </Link>

      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{application.company_name}</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
          <Badge value={application.application_type} styles={TYPE_STYLES} />
          <Badge value={application.status} styles={STATUS_STYLES} />
          <SyncBadge state={application.sync_state} />
          {application.re_verification && (
            <span className="text-xs px-2 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-200">
              re-verification of #{application.parent_id}
            </span>
          )}
          <span className="text-slate-300">·</span>
          <span>submitted {new Date(application.created_at).toLocaleString()}</span>
        </div>
      </div>

      <div className="space-y-4">
        <CompanyMatchPanel
          application={application}
          candidates={candidates}
          onChange={handleApplicationChange}
        />

        <ReviewActions application={application} onChange={handleApplicationChange} />

        <Section title="Company">
          <Row label="Address">{application.full_address || '—'}</Row>
          <Row label="Street">{addr.street1}{addr.street2 ? `, ${addr.street2}` : ''}</Row>
          <Row label="City">{addr.city}</Row>
          <Row label="State / Region">{addr.state}</Row>
          <Row label="Postal code">{addr.postal_code}</Row>
          <Row label="Country">{addr.country}</Row>
          <Row label="Website">{application.company_website}</Row>
          <Row label="Social">{application.company_social_links}</Row>
        </Section>

        <Section title="Contact">
          <Row label="Name">{application.full_name}</Row>
          <Row label="Title">{application.job_title}</Row>
          <Row label="Email">
            {application.email_address ? (
              <Link
                to={`/trade_applications?q=${encodeURIComponent(application.email_address)}`}
                className="text-brand-600 hover:text-brand-700 underline"
                title="Search all applications by this email"
              >
                {application.email_address}
              </Link>
            ) : null}
            <SyncFieldWarning application={application} field="email" />
          </Row>
          <Row label="Phone">
            {application.phone_number}
            <SyncFieldWarning application={application} field="phone" />
          </Row>
        </Section>

        <Section title="Application">
          <Row label="Product scope">
            {(application.product_scope || []).join(', ') || '—'}
          </Row>
          <Row label="Tax exempt">{application.tax_exempt}</Row>
          <Row label="How they heard">
            {(application.hear_about_us || []).join(', ') || '—'}
          </Row>
        </Section>

        <Section title="Documents">
          <Row label="Files">
            {(application.documents || []).length === 0 ? '—' : (
              <ul className="grid gap-3 sm:grid-cols-2">
                {application.documents.map((d) => (
                  <DocumentPreview key={d.id} document={d} onOpen={setViewingDocument} />
                ))}
              </ul>
            )}
          </Row>
        </Section>

        <Section title="Shopify">
          <Row label="Site">{application.shopify_site}</Row>
          <Row label="Customer ID">
            {application.shopify_customer_id ? (
              application.shopify_customer_admin_url ? (
                <a
                  href={application.shopify_customer_admin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-600 hover:text-brand-700 underline break-all"
                >
                  {application.shopify_customer_id}
                </a>
              ) : (
                application.shopify_customer_id
              )
            ) : null}
          </Row>
          <Row label="Company ID">
            {application.shopify_company_id ? (
              application.shopify_company_admin_url ? (
                <a
                  href={application.shopify_company_admin_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand-600 hover:text-brand-700 underline break-all"
                >
                  {application.shopify_company_id}
                </a>
              ) : (
                application.shopify_company_id
              )
            ) : null}
          </Row>
          <Row label="Synced at">
            {application.shopify_synced_at ? new Date(application.shopify_synced_at).toLocaleString() : null}
          </Row>
          <Row label="Sync state">
            {application.sync_state ? <SyncBadge state={application.sync_state} /> : null}
          </Row>
          {application.sync_state === 'failed' && (
            <Row label="Sync error">
              <span className="text-red-700">
                {application.sync_error_field ? `[${application.sync_error_field}] ` : ''}
                {application.sync_error_message}
              </span>
              {application.sync_failed_at && (
                <span className="block text-xs text-slate-500 mt-1">
                  failed {new Date(application.sync_failed_at).toLocaleString()}
                </span>
              )}
            </Row>
          )}
        </Section>

        <Section title="Review">
          <Row label="Reviewer notes">{application.reviewer_notes}</Row>
          <Row label="Reviewed at">
            {application.reviewed_at ? new Date(application.reviewed_at).toLocaleString() : null}
          </Row>
          <Row label="Flagged at">
            {application.flagged_at ? new Date(application.flagged_at).toLocaleString() : null}
          </Row>
          <Row label="Flag reason">{application.flag_reason}</Row>
          <Row label="Revoked at">
            {application.revoked_at ? new Date(application.revoked_at).toLocaleString() : null}
          </Row>
          <Row label="Revocation reason">{application.revocation_reason}</Row>
          <Row label="Fraud score">{application.fraud_score ?? null}</Row>
        </Section>

        <Section title="History">
          {history.length === 0 ? (
            <Row label="Events">—</Row>
          ) : (
            history.map((h, i) => (
              <Row key={i} label={new Date(h.at).toLocaleString()}>
                <span className="font-mono text-xs">
                  {h.from} → {h.to}
                </span>
                <span className="text-slate-500 text-xs ml-2">({h.event})</span>
                {h.actor_email && (
                  <div className="text-xs text-slate-500 mt-0.5">by {h.actor_email}</div>
                )}
                {h.notes && <div className="text-xs text-slate-700 mt-0.5">“{h.notes}”</div>}
              </Row>
            ))
          )}
        </Section>

        {(application.related_applications || []).length > 0 && (
          <div className="card p-5 sm:p-6">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider">
                Other applications by this customer
              </h3>
              <Link
                to={`/trade_applications?q=${encodeURIComponent(application.email_address || '')}`}
                className="text-sm text-brand-600 hover:text-brand-700"
              >
                View all in list →
              </Link>
            </div>
            <ul className="divide-y divide-slate-100">
              {application.related_applications.map((r) => (
                <li key={r.id} className="py-2.5">
                  <Link
                    to={`/trade_applications/${r.id}`}
                    className="grid grid-cols-1 sm:grid-cols-[auto_auto_1fr_auto] gap-2 sm:gap-4 items-center hover:bg-slate-50 -mx-2 px-2 rounded"
                  >
                    <span className="text-sm text-slate-500 font-mono">#{r.id}</span>
                    <Badge value={r.application_type} styles={TYPE_STYLES} />
                    <span className="text-sm text-slate-900 truncate">{r.company_name}</span>
                    <span className="flex items-center gap-2 text-xs text-slate-500 whitespace-nowrap">
                      <Badge value={r.status} styles={STATUS_STYLES} />
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <DangerZone application={application} onChange={handleApplicationChange} />
      </div>

      {viewingDocument && (
        <DocumentViewer document={viewingDocument} onClose={() => setViewingDocument(null)} />
      )}
    </div>
  )
}
